/**
 * Lock screen — shown when the app is locked after going to background.
 *
 * Primary unlock: password input
 * Optional: biometric unlock (if available and user has stored refresh token)
 */

import { useEffect, useState } from 'react';
import { isBiometricAvailable, getStoredRefreshToken } from '../lib/auth/biometric';
import { useAuth } from '../lib/auth/hooks';
import { getSupabase } from '../lib/supabase/client';
import { useOnlineStatus } from '../hooks/useOnlineStatus';

interface LockScreenProps {
  userEmail: string;
  userName: string;
  onUnlock: () => void;
  /** Offline credential verification — provided after Phase 2 (cached credential login) */
  onVerifyOffline?: (email: string, password: string) => Promise<boolean>;
}

const containerStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'var(--color-bg-primary)',
  zIndex: 9999,
  fontFamily: 'var(--font-family-primary)',
  padding: 24,
};

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 360,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 20,
};

const avatarStyle: React.CSSProperties = {
  width: 64,
  height: 64,
  borderRadius: '50%',
  backgroundColor: 'var(--color-tint-blue)',
  color: 'var(--color-label-emphasized)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 24,
  fontWeight: 700,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: 10,
  border: '1px solid var(--color-border-opaque)',
  backgroundColor: 'var(--color-bg-primary)',
  fontSize: 'var(--font-size-body)',
  lineHeight: 'var(--line-height-body)',
  color: 'var(--color-label-primary)',
  fontFamily: 'var(--font-family-primary)',
  outline: 'none',
  boxSizing: 'border-box' as const,
  minHeight: 44,
};

const primaryButtonStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: 10,
  border: 'none',
  backgroundColor: 'var(--color-tint-blue)',
  color: 'var(--color-label-emphasized)',
  fontSize: 'var(--font-size-body)',
  fontWeight: 600,
  fontFamily: 'var(--font-family-primary)',
  minHeight: 44,
  cursor: 'pointer',
};

const secondaryButtonStyle: React.CSSProperties = {
  ...primaryButtonStyle,
  backgroundColor: 'transparent',
  color: 'var(--color-tint-blue)',
  border: '1px solid var(--color-border-opaque)',
};

const linkStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--color-label-tertiary)',
  fontSize: 'var(--font-size-footnote)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-primary)',
  padding: 0,
};

export function LockScreen({ userEmail, userName, onUnlock, onVerifyOffline }: LockScreenProps) {
  const { signIn, signOut } = useAuth();
  const { isOnline } = useOnlineStatus();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);

  useEffect(() => {
    async function checkBiometric() {
      const available = await isBiometricAvailable();
      if (!available) return;
      const token = await getStoredRefreshToken();
      setBiometricAvailable(!!token);
    }
    checkBiometric();
  }, []);

  async function handlePasswordUnlock(e: React.FormEvent) {
    e.preventDefault();
    if (!password.trim()) return;

    setLoading(true);
    setError(null);

    try {
      if (isOnline) {
        await signIn(userEmail, password);
      } else if (onVerifyOffline) {
        const valid = await onVerifyOffline(userEmail, password);
        if (!valid) throw new Error('Neplatne heslo');
      } else {
        throw new Error('Offline odemknuti neni dostupne');
      }
      onUnlock();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Odemknuti selhalo');
    } finally {
      setLoading(false);
    }
  }

  async function handleBiometricUnlock() {
    setLoading(true);
    setError(null);

    try {
      const token = await getStoredRefreshToken();
      if (!token) {
        setError('Biometricke odemknuti neni k dispozici');
        return;
      }

      if (isOnline) {
        const client = getSupabase();
        if (!client) throw new Error('Supabase not configured');
        const { error: refreshError } = await client.auth.refreshSession({ refresh_token: token });
        if (refreshError) throw refreshError;
      }
      // Offline: if refresh token exists, trust the biometric verification
      // (the OS already verified the user via Touch ID / Face ID)

      onUnlock();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Biometricke odemknuti selhalo');
    } finally {
      setLoading(false);
    }
  }

  const initials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={avatarStyle}>{initials || '?'}</div>

        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              fontSize: 'var(--font-size-title3)',
              fontWeight: 600,
              color: 'var(--color-label-primary)',
            }}
          >
            {userName}
          </div>
          <div
            style={{
              fontSize: 'var(--font-size-footnote)',
              color: 'var(--color-label-tertiary)',
              marginTop: 4,
            }}
          >
            Aplikace je zamknuta
          </div>
        </div>

        <form onSubmit={handlePasswordUnlock} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Heslo"
            style={inputStyle}
            autoFocus
            autoComplete="current-password"
          />

          {error && (
            <div
              style={{
                color: 'var(--color-tint-red)',
                fontSize: 'var(--font-size-footnote)',
                textAlign: 'center',
              }}
            >
              {error}
            </div>
          )}

          <button type="submit" disabled={loading || !password.trim()} style={primaryButtonStyle}>
            {loading ? 'Overoviuji...' : 'Odemknout'}
          </button>
        </form>

        {biometricAvailable && (
          <button
            type="button"
            onClick={handleBiometricUnlock}
            disabled={loading}
            style={secondaryButtonStyle}
          >
            Odemknout biometricky
          </button>
        )}

        <button type="button" onClick={() => signOut()} style={linkStyle}>
          Odhlasit se
        </button>
      </div>
    </div>
  );
}
